import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useToast } from '../contexts/toast-context';
import { Task, TaskEvent } from '../types/game-document/entities/task';
import {
  Algorithm,
  AlgorithmControlStructure,
  AlgorithmStep
} from '../types/algorithm';
import { TaskContent } from '../types/game-document/entities/task-content';
import { GetResourceById, GetResourceValue } from '../utils/game-document';
import { GameDocumentContext } from '../contexts/game-document';
import { Timer, TimerEvent } from '../types/game-document/entities/timer';
import { Area, AreaEvent } from '../types/game-document/entities/area';

// events helper
const SIMPLE_OPS_REQUIRE_ASSET_AND_QTY = [
  'distributeTask',
  'giveItem',
  'addBankStock',
  'removeBankStock',
  'removeItem'
];
const SIMPLE_OPS_REQUIRE_QTY_ONLY = ['giveScore', 'removeScore'];
const SIMPLE_OPS_REQUIRE_ASSET_ONLY = [
  'giveTitle',
  'hideArea',
  'hideTask',
  'navigateToZone',
  'openRandomTask',
  'openTask',
  'pauseTimer',
  'removeTitle',
  'resetTask',
  'resumeTimer',
  'randomTask',
  'showArea',
  'showTask',
  'startTimer',
  'stopTimer'
];
const CONDITION_OPS_REQUIRE_QTY_ONLY = ['haveScore', 'greaterOrEqual', 'less'];

const isIncompleteSimpleStep = (step: AlgorithmStep): boolean =>
  !step.identifier ||
  !step.operation ||
  (SIMPLE_OPS_REQUIRE_ASSET_AND_QTY.includes(step.operation) &&
    (!step.argumentAssetId || Number.isNaN(step.argumentQuantity))) ||
  (SIMPLE_OPS_REQUIRE_QTY_ONLY.includes(step.operation) &&
    Number.isNaN(step.argumentQuantity)) ||
  (SIMPLE_OPS_REQUIRE_ASSET_ONLY.includes(step.operation) &&
    !step.argumentAssetId);

const isIncompleteConditionStep = (step: AlgorithmControlStructure): boolean =>
  !step.identifier ||
  step.condition === 'select' ||
  !step.operation ||
  (step.operation === 'haveItem' &&
    (!step.argumentAssetId || Number.isNaN(step.argumentQuantity))) ||
  (CONDITION_OPS_REQUIRE_QTY_ONLY.includes(step.operation) &&
    Number.isNaN(step.argumentQuantity)) ||
  (['choosesAnswer', 'haveRole'].includes(step.operation) &&
    !step.argumentAssetId);

export type IncompleteAlgorithmResult = {
  inCompleteStepIndex: number;
  type?: 'then' | 'else';
} | null;

// This validation will check for each task
// each onOpen, onClose, onComplete, and onProximity
// once found one incomplete, will navigate there and mark with blip the element so designer will know which event that incomplete
// after one is completed or removed by designer, then designer click save again, checker will moving forward to the next incomplete event
// and so on
const validateAlgorithmSteps = (
  algorithm: Algorithm | undefined
): IncompleteAlgorithmResult => {
  const steps = algorithm?.steps;
  if (!steps?.length) return null;

  for (const [parentIndex, step] of steps.entries()) {
    const stepControl = step as AlgorithmControlStructure;

    if (!stepControl.condition) {
      // simple step
      if (isIncompleteSimpleStep(step)) {
        return { inCompleteStepIndex: parentIndex };
      }
    } else {
      // conditional step — check the control itself
      if (isIncompleteConditionStep(stepControl)) {
        return { inCompleteStepIndex: -1 };
      }

      // check compound (AND) conditions
      for (const [idx, compoundStep] of (
        stepControl.ifCompoundStatements ?? []
      ).entries()) {
        if (isIncompleteConditionStep(compoundStep)) {
          return { inCompleteStepIndex: idx };
        }
      }

      // check then
      for (const [idx, ifStep] of (stepControl.ifSteps ?? []).entries()) {
        if (isIncompleteSimpleStep(ifStep)) {
          return {
            inCompleteStepIndex: idx,
            type: 'then'
          };
        }
      }

      // check else
      for (const [idx, ifStep] of (stepControl.elseSteps ?? []).entries()) {
        if (isIncompleteSimpleStep(ifStep)) {
          return {
            inCompleteStepIndex: idx,
            type: 'else'
          };
        }
      }
    }
  }

  return null;
};

export const useInCompleteEvents = () => {
  const { gameId } = useParams();
  const navigate = useNavigate();

  const [state] = React.useContext(GameDocumentContext);

  const { onToastOpen } = useToast();

  const taskEvents = (tasks: Task[], action: 'save' | 'publish' = 'save') => {
    if (!tasks.length) return true;
    let isValid = true;

    const TASK_EVENTS: { key: keyof TaskEvent; openTab: string }[] = [
      { key: 'onOpenTask', openTab: 'task-onopen' },
      { key: 'onCloseTask', openTab: 'task-onclose' },
      { key: 'onCompleteTask', openTab: 'task-oncomplete' },
      { key: 'onProximity', openTab: 'task-onproximity' }
    ];

    const navigateIncomplete = (taskId: string, state: object): void => {
      isValid = false;
      navigate(`/designer/${gameId}/assets/tasks/${taskId}`, {
        state,
        replace: true
      });
      onToastOpen({
        style: 'error',
        title:
          action === 'publish'
            ? 'Game failed to publish.'
            : 'Draft game failed to save.',
        body: `Task behaviour steps are incomplete. Please complete or remove them before ${action === 'publish' ? 'publishing' : 'saving'}.`
      });
    };

    // validation
    for (const task of tasks) {
      for (const { key, openTab } of TASK_EVENTS) {
        const result = validateAlgorithmSteps(task.events?.[key]);
        if (result) {
          navigateIncomplete(task.id, {
            openTab,
            ...result
          });
          return false;
        }
      }
    }

    return isValid;
  };

  const zoneEvents = (action: 'save' | 'publish' = 'save') => {
    const zones = state.gameDocument?.assets.zones;
    if (!zones?.length) return true;

    const ZONE_EVENTS: {
      key: 'onEnterMap' | 'onExitMap' | 'onTaskCompletedMap';
    }[] = [
      { key: 'onEnterMap' },
      { key: 'onExitMap' },
      { key: 'onTaskCompletedMap' }
    ];

    for (const zone of zones) {
      for (const { key } of ZONE_EVENTS) {
        const result = validateAlgorithmSteps(zone.events?.[key]);
        if (result) {
          navigate(`/designer/${gameId}/map/${zone.id}`, {
            state: {
              openEvents: true,
              eventType: key,
              ...result
            },
            replace: true
          });
          const zoneTitle = GetResourceValue(
            state.gameDocument!,
            zone.titleResId ?? ''
          );
          onToastOpen({
            style: 'error',
            title:
              action === 'publish'
                ? 'Game failed to publish.'
                : 'Draft game failed to save.',
            body: `Zone map behaviour steps are incomplete in zone "${zoneTitle}". Please complete or remove them before ${action === 'publish' ? 'publishing' : 'saving'}.`
          });
          return false;
        }
      }
    }
    return true;
  };

  const worldMapEvents = (action: 'save' | 'publish' = 'save') => {
    const events = state.gameDocument?.rules.worldMap.events;
    if (!events) return true;

    const WORLD_MAP_EVENTS: {
      key: 'onEnterMap' | 'onExitMap' | 'onTaskCompletedMap';
    }[] = [
      { key: 'onEnterMap' },
      { key: 'onExitMap' },
      { key: 'onTaskCompletedMap' }
    ];

    for (const { key } of WORLD_MAP_EVENTS) {
      const result = validateAlgorithmSteps(events[key]);
      if (result) {
        navigate(`/designer/${gameId}/map`, {
          state: {
            openEvents: true,
            eventType: key,
            ...result
          },
          replace: true
        });
        onToastOpen({
          style: 'error',
          title:
            action === 'publish'
              ? 'Game failed to publish.'
              : 'Draft game failed to save.',
          body: `Map behaviour steps are incomplete in World Map. Please complete or remove them before ${action === 'publish' ? 'publishing' : 'saving'}.`
        });
        return false;
      }
    }
    return true;
  };

  const timerEvents = (
    timers: Timer[],
    action: 'save' | 'publish' = 'save'
  ) => {
    if (!timers.length) return true;
    let isValid = true;

    const TIMER_EVENTS: {
      key: keyof TimerEvent;
    }[] = [
      { key: 'onStartTimer' },
      { key: 'onElapsedTimer' },
      { key: 'onStopTimer' }
    ];

    const navigateIncomplete = (timerId: string, state: object): void => {
      isValid = false;
      navigate(`/designer/${gameId}/assets/timers/${timerId}`, {
        state,
        replace: true
      });
      onToastOpen({
        style: 'error',
        title:
          action === 'publish'
            ? 'Game failed to publish.'
            : 'Draft game failed to save.',
        body: `Timer behaviour steps are incomplete. Please complete or remove them before ${action === 'publish' ? 'publishing' : 'saving'}.`
      });
    };

    // validation
    for (const timer of timers) {
      for (const { key } of TIMER_EVENTS) {
        const result = validateAlgorithmSteps(timer.events?.[key]);
        if (result) {
          navigateIncomplete(timer.id, {
            ...result
          });
          return false;
        }
      }
    }

    return isValid;
  };

  const areaEvents = (areas: Area[], action: 'save' | 'publish' = 'save') => {
    if (!areas.length) return true;
    let isValid = true;

    const AREA_EVENTS: { key: keyof AreaEvent; openTab: string }[] = [
      { key: 'onEntry', openTab: 'area-onentry' },
      { key: 'onExit', openTab: 'area-onexit' }
    ];

    const navigateIncomplete = (areaId: string, state: object): void => {
      isValid = false;
      navigate(`/designer/${gameId}/assets/areas/${areaId}`, {
        state,
        replace: true
      });
      onToastOpen({
        style: 'error',
        title:
          action === 'publish'
            ? 'Game failed to publish.'
            : 'Draft game failed to save.',
        body: `Area behaviour steps are incomplete. Please complete or remove them before ${action === 'publish' ? 'publishing' : 'saving'}.`
      });
    };

    // validation
    for (const area of areas) {
      for (const { key, openTab } of AREA_EVENTS) {
        const result = validateAlgorithmSteps(area.events?.[key]);
        if (result) {
          navigateIncomplete(area.id, {
            openTab,
            ...result
          });
          return false;
        }
      }
    }

    return isValid;
  };

  const taskContents = (
    taskContents: TaskContent[],
    action: 'save' | 'publish' = 'save'
  ) => {
    if (!taskContents.length) return true;
    let isValid = true;

    const navigateIncomplete = (
      taskContentId: string,
      state: object,
      message?: string
    ): void => {
      isValid = false;
      navigate(`/designer/${gameId}/assets/task-content/${taskContentId}`, {
        state,
        replace: true
      });
      onToastOpen({
        style: 'error',
        title:
          action === 'publish'
            ? 'Game failed to publish.'
            : 'Draft game failed to save.',
        body:
          message ??
          `There is an incomplete task content. Please complete them before ${action === 'publish' ? 'publishing' : 'saving'}.`
      });
    };

    for (const taskContent of taskContents) {
      if (taskContent.id === '-') continue;

      const title = GetResourceById(
        state.gameDocument!,
        taskContent.titleResId ?? ''
      );

      if (!title || title.value === '') {
        navigateIncomplete(taskContent.id, {
          tab: 'information',
          field: 'title'
        });
        return;
      }

      if (!taskContent.forms || !taskContent.forms.length) continue;
      for (const [idx, form] of taskContent.forms.entries()) {
        if (form.questionResId) {
          const qRes = GetResourceById(state.gameDocument!, form.questionResId);
          if (qRes?.value && qRes.value.length > 400) {
            navigateIncomplete(
              taskContent.id,
              {
                tab: 'form',
                field: 'question',
                inCompleteFormIndex: idx
              },
              'Please reduce your text to 400 characters or fewer.'
            );
            return false;
          }
        }

        if (!form.mediaOnlyIdentifierResId) continue;

        const identifier = GetResourceById(
          state.gameDocument!,
          form.mediaOnlyIdentifierResId
        );

        if (
          identifier?.value === '' ||
          (identifier?.value && identifier.value.length > 15)
        ) {
          navigateIncomplete(taskContent.id, {
            tab: 'form',
            field: 'media-only-identifier',
            inCompleteFormIndex: idx
          });
        }
      }
    }

    return isValid;
  };

  return {
    taskEvents,
    taskContents,
    timerEvents,
    areaEvents,
    zoneEvents,
    worldMapEvents
  };
};
